import { Analytics } from '@vercel/analytics/react';
import { useState, useCallback, useEffect } from "react";
import "@/App.css";

// Mailchimp configuration
const MAILCHIMP_URL = "https://laurencejones.us5.list-manage.com/subscribe/post-json";
const MAILCHIMP_U = "64f5984306703bd9fe86be9cc";
const MAILCHIMP_ID = "87e68f1bd4";

// JSONP helper for Mailchimp submission
const submitToMailchimp = (email) => {
  return new Promise((resolve, reject) => {
    const callbackName = `mailchimp_callback_${Date.now()}`;
    const script = document.createElement("script");
    let resolved = false;
    
    window[callbackName] = (response) => {
      resolved = true;
      cleanup();
      
      if (response.result === "success") {
        resolve({ success: true, message: "Thank you — you're on the list" });
      } else if (response.msg && response.msg.includes("already subscribed")) {
        resolve({ success: true, message: "You're already subscribed" });
      } else {
        let errorMsg = response.msg || "Something went wrong";
        errorMsg = errorMsg.replace(/^\d+\s*-\s*/, "");
        reject({ success: false, message: errorMsg });
      }
    };
    
    const cleanup = () => {
      if (window[callbackName]) delete window[callbackName];
      if (script.parentNode) script.parentNode.removeChild(script);
    };
    
    const params = new URLSearchParams({
  u: MAILCHIMP_U,
  id: MAILCHIMP_ID,
  EMAIL: email,
  c: callbackName,
});
    
    script.src = `${MAILCHIMP_URL}?${params.toString()}`;
    script.async = true;
    script.onerror = () => {
      if (!resolved) {
        cleanup();
        reject({ success: false, message: "Network error. Please try again." });
      }
    };
    
    document.body.appendChild(script);
    setTimeout(() => {
      if (!resolved) {
        cleanup();
        reject({ success: false, message: "Request timed out. Please try again." });
      }
    }, 10000);
  });
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Email Form Hook
const useEmailForm = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (honeypot) {
      setStatus("success");
      setMessage("Thank you — you're on the list");
      setEmail("");
      return;
    }
    
    if (!email || !isValidEmail(email)) {
      setStatus("error");
      setMessage("Please enter a valid email");
      return;
    }
    
    setStatus("loading");
    setMessage("");
    
    try {
      const result = await submitToMailchimp(email);
      setStatus("success");
      setMessage(result.message);
      setEmail("");
    } catch (error) {
      setStatus("error");
      setMessage(error.message || "Something went wrong");
    }
  }, [email, honeypot]);

  return { email, setEmail, status, message, honeypot, setHoneypot, handleSubmit };
};

// Header
const Header = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`site-header ${scrolled ? 'scrolled' : ''}`} data-testid="header">
      <span className="logo-mark text-sm opacity-70" data-testid="header-logo">
        Accession OS
      </span>
      <nav className="header-nav">
        <a href="mailto:hello@accession.art" className="nav-link" data-testid="nav-contact">
          Contact
        </a>
      </nav>
    </header>
  );
};

// Email Form
const EmailForm = ({ testIdPrefix }) => {
  const { email, setEmail, status, message, honeypot, setHoneypot, handleSubmit } = useEmailForm();

  return (
    <form onSubmit={handleSubmit} data-testid={`${testIdPrefix}-form`}>
      <input
        type="text"
        name="b_64f5984306703bd9fe86be9cc_9e98b13d4c"
        tabIndex="-1"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        style={{ position: 'absolute', left: '-5000px' }}
        aria-hidden="true"
        autoComplete="off"
      />
      <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-start sm:items-end">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="form-input-premium flex-1 w-full sm:w-auto"
          required
          disabled={status === "loading"}
          data-testid={`${testIdPrefix}-input`}
        />
        <button 
          type="submit" 
          className="form-button-premium"
          disabled={status === "loading"}
          data-testid={`${testIdPrefix}-button`}
        >
          {status === "loading" ? "..." : "Subscribe"}
        </button>
      </div>
      {message && (
        <p 
          className={`form-message-premium ${status === "error" ? "error" : ""}`}
          data-testid={`${testIdPrefix}-message`}
        >
          {message}
        </p>
      )}
    </form>
  );
};

// Accession Hero Section
const AccessionSection = () => {
  return (
    <section id="accession" className="arcanum-section" data-testid="accession-section">
      <div className="page-container section-padding relative z-10">
        <div className="max-w-2xl">
          
          {/* Logo */}
          <span className="logo-mark block text-lg md:text-xl mb-16 md:mb-20 opacity-55" data-testid="accession-logo">
            Accession OS
          </span>

          {/* Headline */}
          <h1 className="headline-primary mb-6 md:mb-8" data-testid="accession-title">
            A system of record for artworks.
          </h1>

          {/* Supporting line */}
          <p className="body-primary mb-16 md:mb-20" data-testid="accession-description">
            Accession OS records the lifecycle of artworks.
          </p>
          
          {/* Email Form */}
          <div className="max-w-sm">
            <p className="label-small mb-4">Stay informed</p>
            <EmailForm testIdPrefix="accession-email" />
            <p className="helper-text mt-6">Occasional updates.</p>
          </div>
          
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="scroll-indicator" data-testid="scroll-indicator">
        <svg width="16" height="9" viewBox="0 0 16 9" fill="none">
          <path d="M1 1L8 8L15 1" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    </section>
  );
};

// System Positioning Section
const SystemSection = () => {
  return (
    <section className="system-section" data-testid="system-section">
      <div className="page-container py-20 md:py-28">
        <p className="system-text" data-testid="system-text">
          A system for recording artworks and the events around them.
        </p>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="site-footer" data-testid="footer">
      <span className="logo-mark text-xs opacity-35" data-testid="footer-accession-logo">
        Accession OS
      </span>
      <span className="text-xs tracking-wider opacity-25" data-testid="footer-location">
        London
      </span>
    </footer>
  );
};

// App
function App() {
  return (
    <div className="min-h-screen" data-testid="app">
      <Header />
      <main>
        <AccessionSection />
        <SystemSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
